// =============================================================================
// hooks/crm/livechatTimeline.ts — Traduz o marcador técnico do live chat na
// timeline do CRM (F49-S03).
//
// O achado (Rogério, 08/09/2026): `interactions.content` gravado pela ponte
// livechat→CRM (`insertInteractionBridge`, apps/api/src/modules/livechat/
// repo.ts) tem a forma "[livechat] type=text ref=<uuid>" DE PROPÓSITO — LGPD
// §8.5 proíbe duplicar o texto real da mensagem (que vive em `messages`,
// atrás do RBAC do live chat) numa segunda tabela. O dado está certo; a
// timeline é que não pode renderizar esse marcador cru para o operador.
//
// Este módulo é puro (sem React) para poder ser reutilizado por qualquer tela
// que também renderize `LeadInteraction.content` — hoje isso inclui, além da
// timeline do CRM (CrmDetailPage.tsx, dentro do escopo deste slot),
// `features/sales/agenda/components/AppointmentDrawer.tsx` ("Últimos
// eventos"), que está FORA de `files_allowed` deste slot e continua exibindo
// o marcador cru. Reportado no PR — é outra tela a corrigir, não descoberta
// tardia dentro deste arquivo.
//
// Limitação conhecida (documentar, não inventar): a API (GET
// /api/leads/:id/interactions, ver apps/api/src/modules/leads/service.ts)
// não expõe nem `metadata` nem `conversationId` para esta linha — apenas
// `id/leadId/type/content/actorName/createdAt` (LeadInteractionResponseSchema
// em apps/api/src/modules/leads/schemas.ts). `insertInteractionBridge` nem
// recebe o `conversationId` da conversa (só `messageId` opaco). Por isso:
//   - O "message_type" e o "ref" (messageId) usados aqui vêm de um PARSE do
//     próprio `content` — que já é estruturado sem PII (é o mesmo dado que o
//     backend persiste em `metadata.message_type`/`metadata.message_id`,
//     só que a API não os projeta separadamente hoje).
//   - O link "ver no atendimento" não consegue abrir a conversa exata (não
//     há `conversationId` disponível no front) — leva ao inbox do live chat,
//     de acesso controlado e auditado. Deep-link exato é follow-up de
//     backend (persistir `conversationId` na interaction), fora deste slot.
//   - O agrupamento é por "interações consecutivas do live chat no mesmo dia
//     civil" (fuso da organização) — aproximação de "mesma conversa" sem
//     acesso a um `conversationId` real. Uma interação não-livechat no meio
//     (nota, mudança de estágio) sempre quebra o grupo.
// =============================================================================

import { MessageTypeSchema, type MessageType } from '@elemento/shared-schemas';

import { orgDayKey } from '../../lib/orgDateTime';

import type { LeadInteraction } from './types';

// ─── Parse do marcador técnico ────────────────────────────────────────────────

const LIVECHAT_MARKER_RE = /^\[livechat\] type=(\S+) ref=(\S+)$/;

export interface LivechatMarker {
  /** Tipo de mensagem cru (pode não estar no enum conhecido — ver `KNOWN_MESSAGE_TYPES`). */
  messageType: string;
  /** UUID opaco da mensagem em `messages` — nunca é PII (doc 17 §8.5). */
  messageId: string;
}

/**
 * Reconhece e decompõe "[livechat] type=<tipo> ref=<uuid>". `null` para
 * qualquer outro formato de `content` (nota manual, ligação, resumo de
 * simulação, etc.) — essas continuam exibindo o `content` normalmente.
 */
export function parseLivechatMarker(content: string): LivechatMarker | null {
  const match = LIVECHAT_MARKER_RE.exec(content);
  if (match === null) return null;
  const [, messageType, messageId] = match;
  if (messageType === undefined || messageId === undefined) return null;
  return { messageType, messageId };
}

/**
 * `true` quando a interação é uma ponte do live chat (nunca renderizar
 * `content` cru). Detecta pelo PRÓPRIO formato do marcador — não pelo
 * `type` — porque `insertInteractionBridge` aceita canais além de
 * `whatsapp`/`chatwoot` (`phone`, `email`, `in_person`; ver
 * `InsertInteractionBridgeParams` em apps/api/src/modules/livechat/repo.ts)
 * e o mapeamento `CHANNEL_TO_TYPE` do backend nem sempre resolve para
 * `'whatsapp'` nesses casos. O formato "[livechat] type=<tipo> ref=<uuid>"
 * é específico o bastante para não colidir com conteúdo humano de nota ou
 * ligação.
 */
export function isLivechatInteraction(interaction: LeadInteraction): boolean {
  return parseLivechatMarker(interaction.content) !== null;
}

const KNOWN_MESSAGE_TYPES = new Set<string>(MessageTypeSchema.options);

function isKnownMessageType(value: string): value is MessageType {
  return KNOWN_MESSAGE_TYPES.has(value);
}

// ─── Direção ──────────────────────────────────────────────────────────────────

export type InteractionDirection = 'inbound' | 'outbound';

/**
 * A API não expõe `direction` na resposta de interações (`LeadInteractionResponseSchema`)
 * — só `actorName`, já derivado dela no backend
 * (`r.direction === 'inbound' ? 'Cliente' : 'Equipe'`, ver
 * apps/api/src/modules/leads/service.ts). Reaplicamos a mesma regra aqui em
 * vez de adivinhar por outro campo.
 */
export function inferDirection(interaction: LeadInteraction): InteractionDirection {
  return interaction.actorName === 'Cliente' ? 'inbound' : 'outbound';
}

// ─── Rótulo humano ────────────────────────────────────────────────────────────

const MESSAGE_TYPE_LABELS: Record<MessageType, { inbound: string; outbound: string }> = {
  text: { inbound: 'Mensagem recebida pelo WhatsApp', outbound: 'Mensagem enviada pelo WhatsApp' },
  image: { inbound: 'Foto recebida pelo WhatsApp', outbound: 'Foto enviada pelo WhatsApp' },
  video: { inbound: 'Vídeo recebido pelo WhatsApp', outbound: 'Vídeo enviado pelo WhatsApp' },
  audio: { inbound: 'Áudio recebido pelo WhatsApp', outbound: 'Áudio enviado pelo WhatsApp' },
  voice: { inbound: 'Áudio recebido pelo WhatsApp', outbound: 'Áudio enviado pelo WhatsApp' },
  document: {
    inbound: 'Documento recebido pelo WhatsApp',
    outbound: 'Documento enviado pelo WhatsApp',
  },
  sticker: {
    inbound: 'Figurinha recebida pelo WhatsApp',
    outbound: 'Figurinha enviada pelo WhatsApp',
  },
  location: {
    inbound: 'Localização recebida pelo WhatsApp',
    outbound: 'Localização enviada pelo WhatsApp',
  },
  contact: { inbound: 'Contato recebido pelo WhatsApp', outbound: 'Contato enviado pelo WhatsApp' },
  interactive: {
    inbound: 'Mensagem interativa recebida pelo WhatsApp',
    outbound: 'Mensagem interativa enviada pelo WhatsApp',
  },
  template: { inbound: 'Modelo recebido pelo WhatsApp', outbound: 'Modelo enviado pelo WhatsApp' },
  reaction: { inbound: 'Reação recebida pelo WhatsApp', outbound: 'Reação enviada pelo WhatsApp' },
  system: { inbound: 'Evento do sistema no WhatsApp', outbound: 'Evento do sistema no WhatsApp' },
  story_mention: { inbound: 'Menção em story recebida', outbound: 'Menção em story enviada' },
  story_reply: { inbound: 'Resposta a story recebida', outbound: 'Resposta a story enviada' },
  share: {
    inbound: 'Compartilhamento recebido pelo WhatsApp',
    outbound: 'Compartilhamento enviado pelo WhatsApp',
  },
  comment: { inbound: 'Comentário recebido', outbound: 'Comentário enviado' },
  comment_reply: {
    inbound: 'Resposta a comentário recebida',
    outbound: 'Resposta a comentário enviada',
  },
  ig_postback: { inbound: 'Interação recebida', outbound: 'Interação enviada' },
  referral: { inbound: 'Indicação recebida', outbound: 'Indicação enviada' },
};

/** Rótulo honesto — genérico para qualquer `message_type` fora do enum conhecido (nunca inventa detalhe). */
const GENERIC_LABEL = 'Mensagem no WhatsApp';

export function livechatMessageLabel(messageType: string, direction: InteractionDirection): string {
  if (isKnownMessageType(messageType)) {
    return MESSAGE_TYPE_LABELS[messageType]?.[direction] ?? GENERIC_LABEL;
  }
  return GENERIC_LABEL;
}

// ─── Agrupamento de interações consecutivas ──────────────────────────────────

/** Um card de interação normal (não agrupado) — não-livechat OU livechat isolada (sem vizinhas no mesmo dia). */
export interface TimelineSingleItem {
  kind: 'single';
  interaction: LeadInteraction;
}

/** Um card agrupado — N interações consecutivas do live chat no mesmo dia civil. */
export interface TimelineGroupItem {
  kind: 'livechat-group';
  /** Estável entre renders — id da interação mais recente do grupo. */
  id: string;
  count: number;
  /** ISO da interação mais antiga do grupo. */
  firstAt: string;
  /** ISO da interação mais recente do grupo (usada para ordenar o card na timeline). */
  lastAt: string;
  /** `messageId` da interação mais recente — usado para "ver no atendimento". */
  messageId: string;
}

export type TimelineDisplayItem = TimelineSingleItem | TimelineGroupItem;

/**
 * Agrupa interações consecutivas do live chat no mesmo dia civil (fuso da
 * organização) num único card. `interactions` deve já vir ordenado do mais
 * recente para o mais antigo (mesma ordem da timeline, ver
 * `mergedInteractions` em CrmDetailPage.tsx) — a função preserva essa ordem.
 *
 * "Consecutivas" == nenhuma outra interação (nota, mudança de estágio,
 * compromisso, ligação) entre elas — ver limitação de `conversationId` no
 * cabeçalho do arquivo.
 */
export function buildTimelineDisplayItems(
  interactions: readonly LeadInteraction[],
  timeZone?: string,
): TimelineDisplayItem[] {
  const result: TimelineDisplayItem[] = [];
  let i = 0;

  while (i < interactions.length) {
    const current = interactions[i];
    if (current === undefined) break;

    if (!isLivechatInteraction(current)) {
      result.push({ kind: 'single', interaction: current });
      i += 1;
      continue;
    }

    const dayKey = orgDayKey(current.createdAt, timeZone);
    const run: LeadInteraction[] = [current];
    let j = i + 1;
    while (j < interactions.length) {
      const next = interactions[j];
      if (next === undefined) break;
      if (!isLivechatInteraction(next)) break;
      if (orgDayKey(next.createdAt, timeZone) !== dayKey) break;
      run.push(next);
      j += 1;
    }

    if (run.length === 1) {
      result.push({ kind: 'single', interaction: current });
    } else {
      const oldest = run[run.length - 1];
      const marker = parseLivechatMarker(current.content);
      result.push({
        kind: 'livechat-group',
        id: `livechat-group-${current.id}`,
        count: run.length,
        firstAt: (oldest ?? current).createdAt,
        lastAt: current.createdAt,
        // marker sempre existe aqui (run só contém interações livechat válidas).
        messageId: marker?.messageId ?? current.id,
      });
    }
    i = j;
  }

  return result;
}
